import type { FC, KeyboardEvent, ReactNode, SyntheticEvent } from "react";
import { ButtonUtility } from "@/shared/components/button-utility";
import { cx } from "@/utils/cx";

export type TableRowAction = {
	/** Identificador estável da ação na linha. Usa o rótulo quando omitido. */
	id?: string;
	/** Nome acessível e texto do tooltip. */
	label: string;
	icon?: FC<{ className?: string }>;
	onClick: () => void;
	/**
	 * Ação indisponível. O botão continua focável com `aria-disabled`, mantém o
	 * tooltip e não executa a ação por clique ou teclado.
	 */
	isDisabled?: boolean;
};

export type TableRowActionsProps = {
	/** Ações diretas, no padrão de Documentos de Veículos. */
	actions?: TableRowAction[];
	/**
	 * Conteúdo adicional depois das ações diretas, como um menu de ações
	 * condicionais. Recebe a mesma proteção contra a ação da linha.
	 */
	children?: ReactNode;
	/** Nome acessível do grupo, por exemplo "Ações do veículo ABC1D23". */
	label?: string;
	className?: string;
};

const stopPropagation = (event: SyntheticEvent) => {
	event.stopPropagation();
};

/** Teclas que ativam a linha; as setas continuam disponíveis para a navegação da tabela. */
const stopActivationKeys = (event: KeyboardEvent) => {
	if (event.key === "Enter" || event.key === " ") {
		event.stopPropagation();
	}
};

/**
 * Ações de uma linha de tabela.
 *
 * Usa `ButtonUtility` terciário `xs` com tooltip e `gap-1`, como em Documentos.
 * Cliques, ponteiro e Enter/Espaço dentro do grupo não chegam à linha, então a
 * navegação por linha (`onAction`/`onRowClick`) não dispara junto com uma ação.
 */
export const TableRowActions = ({
	actions = [],
	children,
	label,
	className,
}: TableRowActionsProps) => (
	// biome-ignore lint/a11y/noNoninteractiveElementInteractions: os handlers só impedem que eventos das ações cheguem à linha; o grupo não é um controle.
	// biome-ignore lint/a11y/useSemanticElements: fieldset é para campos de formulário; aqui o grupo reúne botões de uma linha da tabela.
	<div
		aria-label={label}
		className={cx("flex items-center gap-1", className)}
		data-slot="table-row-actions"
		onClick={stopPropagation}
		onKeyDown={stopActivationKeys}
		onMouseDown={stopPropagation}
		onPointerDown={stopPropagation}
		onPointerUp={stopPropagation}
		role="group"
	>
		{actions.map((action) => (
			<ButtonUtility
				className="text-primary"
				color="tertiary"
				disabledBehavior="focusable"
				icon={action.icon}
				isDisabled={action.isDisabled}
				key={action.id ?? action.label}
				onClick={action.onClick}
				size="xs"
				tooltip={action.label}
			/>
		))}
		{children}
	</div>
);
